/**
 * 営業・通知設定 > 自動通知タブ (2026-10-26 に「自動メール」から改名し、「フォーム通知」を統合)。
 *
 * settings/sales に保存する:
 *  - autoMailFromAccountId          … 自動メールのデフォルト送信元
 *  - accountingEmail                … 経理担当メールアドレス
 *  - accountingChatworkEndpointId   … 経理担当 Chatwork (設定 > 通知 > チャット管理 の Chatwork から選ぶ)
 *  - accountingGoogleChatEndpointId … 経理担当 Google Chat (同 Google Chat から選ぶ)
 *  - accountingNotify               … 経理担当への通知を、きっかけごとに メール / GC / CW で送るか
 *  - formReplyNotifyChannel         … 返答待ち回答の通知先 (旧「フォーム通知」タブ)
 */
import type {
  AccountingNotifyChannels,
  AccountingNotifyTrigger,
  FormReplyNotifyChannel,
} from '@goenhub/shared';
import {
  ACCOUNTING_NOTIFY_CHANNEL_LABELS,
  ACCOUNTING_NOTIFY_TRIGGERS,
  FORM_REPLY_NOTIFY_CHANNEL_DEFAULT,
  FORM_REPLY_NOTIFY_CHANNEL_LABELS,
  resolveAccountingNotifyChannels,
} from '@goenhub/shared';
import { Info } from 'lucide-react';
import { useEffect, useState } from 'react';

import { AccountingNoticeExample } from './AccountingNoticeExample';

import { Button } from '@/components/ui/button';
import { Input, Label } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { toast } from '@/components/ui/toast';
import { useChatEndpoints } from '@/hooks/useChatEndpoints';
import { useMailAccounts } from '@/hooks/useMailAccounts';
import { useSalesSettingsWithDefaults, useUpdateSalesSettings } from '@/hooks/useSettings';

type NotifyState = Record<AccountingNotifyTrigger, AccountingNotifyChannels>;

const CHANNEL_KEYS: Array<keyof AccountingNotifyChannels> = ['mail', 'googleChat', 'chatwork'];

/** 返答待ち回答の通知先: 選択肢の表示順と補足説明。 */
const FORM_CHANNEL_OPTIONS: Array<{
  value: FormReplyNotifyChannel;
  description: string;
}> = [
  {
    value: 'board',
    description:
      '掲示板の「お問合せ対応」カテゴリーへ作業依頼として自動投稿します（宛先=営業部署の社員／緊急度=中／期日=4日後）。',
  },
  {
    value: 'mail',
    description: '掲示板へは投稿せず、自動投稿と同じ件名・本文をメールで送ります。',
  },
  { value: 'both', description: '掲示板への自動投稿とメール通知の両方を行います。' },
  {
    value: 'none',
    description: '通知しません。回収一覧の「返答待ち」ラベルは従来どおり付きます。',
  },
];

function SectionTitle({ title, description }: { title: string; description?: string }) {
  return (
    <div className="mb-3">
      <h3 className="text-sm font-bold text-[var(--text-primary)]">{title}</h3>
      {description && (
        <p className="text-xs text-[var(--text-muted)] mt-0.5 leading-relaxed">{description}</p>
      )}
    </div>
  );
}

/** ピル型のオン/オフボタン。クリックでハイライト、再度クリックでオフ。 */
function ChannelPill({ on, label, onClick }: { on: boolean; label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      aria-pressed={on}
      onClick={onClick}
      className={`min-w-[52px] px-3 py-1 rounded-full text-xs font-semibold border transition-colors ${
        on
          ? 'bg-[var(--accent)] text-white border-[var(--accent)]'
          : 'border-[var(--border)] bg-[var(--bg-surface)] text-[var(--text-muted)] hover:bg-[var(--bg-hover)]'
      }`}
    >
      {label}
    </button>
  );
}

export function AutoNotifyTab() {
  const { merged, data: salesData, isLoading: loadingSettings } = useSalesSettingsWithDefaults();
  const { data: accounts, isLoading: loadingAccounts } = useMailAccounts();
  const chatworkEndpoints = useChatEndpoints('chatwork');
  const googleChatEndpoints = useChatEndpoints('google_chat');
  const update = useUpdateSalesSettings();

  const [accountId, setAccountId] = useState<string>('');
  const [accountingEmail, setAccountingEmail] = useState<string>('');
  const [chatworkId, setChatworkId] = useState<string>('');
  const [googleChatId, setGoogleChatId] = useState<string>('');
  const [notify, setNotify] = useState<NotifyState>(() => buildNotifyState(undefined));
  const [openExample, setOpenExample] = useState<AccountingNotifyTrigger | null>(null);
  const [formChannel, setFormChannel] = useState<FormReplyNotifyChannel>(
    FORM_REPLY_NOTIFY_CHANNEL_DEFAULT,
  );

  useEffect(() => {
    setAccountId(merged.autoMailFromAccountId ?? '');
    setAccountingEmail(merged.accountingEmail ?? '');
    setChatworkId(merged.accountingChatworkEndpointId ?? '');
    setGoogleChatId(merged.accountingGoogleChatEndpointId ?? '');
    setNotify(buildNotifyState(merged.accountingNotify));
    setFormChannel(merged.formReplyNotifyChannel ?? FORM_REPLY_NOTIFY_CHANNEL_DEFAULT);
    // 読込完了時・保存後の再取得時にフォームを最新化する
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [salesData]);

  const handleSave = async () => {
    const trimmedAccounting = accountingEmail.trim();
    if (trimmedAccounting && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedAccounting)) {
      toast.error('経理担当メールアドレスの形式が不正です');
      return;
    }
    try {
      await update.mutateAsync({
        autoMailFromAccountId: accountId || null,
        accountingEmail: trimmedAccounting || null,
        accountingChatworkEndpointId: chatworkId || null,
        accountingGoogleChatEndpointId: googleChatId || null,
        accountingNotify: notify,
        formReplyNotifyChannel: formChannel,
      });
      toast.success('保存しました');
    } catch (err) {
      toast.error(`失敗: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  if (loadingSettings || loadingAccounts) return <Spinner />;

  const usableAccounts = (accounts ?? []).filter((a) => a.status === 'active');
  // 返答待ち回答のメール宛先 = 自動メール用デフォルト送信元（未指定なら既定アカウント）
  const autoMailAccount =
    usableAccounts.find((a) => a.id === accountId) ??
    usableAccounts.find((a) => a.isDefault) ??
    usableAccounts[0];
  const formMailSelected = formChannel === 'mail' || formChannel === 'both';

  const cwList = chatworkEndpoints.data ?? [];
  const gcList = googleChatEndpoints.data ?? [];
  const selectedCw = cwList.find((e) => e.id === chatworkId) ?? null;
  const selectedGc = gcList.find((e) => e.id === googleChatId) ?? null;

  // 「オンなのに送信先が無い」チャネルの警告文
  const missingTarget = (key: keyof AccountingNotifyChannels): string | null => {
    if (key === 'mail') return accountingEmail.trim() ? null : '経理担当メールアドレスが未入力です';
    if (key === 'chatwork') {
      if (!selectedCw) return '経理担当チャットワークが未選択です';
      if (!selectedCw.isActive) return '選択中のチャットワークが無効になっています';
      return null;
    }
    if (!selectedGc) return '経理担当 Google Chat が未選択です';
    if (!selectedGc.isActive) return '選択中の Google Chat が無効になっています';
    return null;
  };

  const togglePill = (trigger: AccountingNotifyTrigger, key: keyof AccountingNotifyChannels) =>
    setNotify((prev) => ({
      ...prev,
      [trigger]: { ...prev[trigger], [key]: !prev[trigger][key] },
    }));

  const selectClass =
    'mt-1 w-full px-3 py-2 text-sm bg-[var(--bg-elevated)] border border-[var(--border)] rounded-sm';

  return (
    <div className="max-w-3xl space-y-8">
      {/* ── 送信元 ── */}
      <section>
        <SectionTitle title="送信元" />
        <Label htmlFor="auto-mail-from">送信元メールアドレス（自動メール用デフォルト）</Label>
        <select
          id="auto-mail-from"
          value={accountId}
          onChange={(e) => setAccountId(e.target.value)}
          className={selectClass}
        >
          <option value="">（指定なし — 既定アカウントを使用）</option>
          {usableAccounts.map((a) => (
            <option key={a.id} value={a.id}>
              {a.displayName} {`<${a.email}>`}
              {a.isDefault ? ' [既定]' : ''}
            </option>
          ))}
        </select>
        <p className="text-xs text-[var(--text-muted)] mt-1">
          見積送信・請求送信などの自動メールで使用するデフォルト送信元。個別の業務メールテンプレートで送信元が指定されている場合はそちらが優先されます。
        </p>
        {usableAccounts.length === 0 && (
          <p className="text-xs text-[var(--accent-amber)] mt-1">
            送信可能なメールアカウントがありません。先に「メール設定 &gt;
            送信元アカウント」で登録してください。
          </p>
        )}
      </section>

      {/* ── 経理担当 ── */}
      <section className="border-t border-[var(--border)] pt-6 space-y-4">
        <SectionTitle
          title="経理担当"
          description="送金依頼・報酬の支払い申請など、経理担当への通知の宛先と送り方を設定します。"
        />

        <div>
          <Label htmlFor="accounting-email">経理担当メールアドレス</Label>
          <Input
            id="accounting-email"
            type="email"
            inputMode="email"
            placeholder="例: keiri@example.com"
            value={accountingEmail}
            onChange={(e) => setAccountingEmail(e.target.value)}
          />
        </div>

        <div>
          <Label htmlFor="accounting-chatwork">経理担当チャットワーク</Label>
          <select
            id="accounting-chatwork"
            value={chatworkId}
            onChange={(e) => setChatworkId(e.target.value)}
            className={selectClass}
          >
            <option value="">（未選択）</option>
            {cwList.map((e) => (
              <option key={e.id} value={e.id}>
                {e.name}（ルーム ID: {e.roomId ?? '-'}）{e.isActive ? '' : ' [無効]'}
              </option>
            ))}
          </select>
          <p className="text-xs text-[var(--text-muted)] mt-1">
            設定 &gt; 通知 &gt; チャット管理 の Chatwork に登録したものから選びます。
          </p>
        </div>

        <div>
          <Label htmlFor="accounting-google-chat">経理担当 Google Chat</Label>
          <select
            id="accounting-google-chat"
            value={googleChatId}
            onChange={(e) => setGoogleChatId(e.target.value)}
            className={selectClass}
          >
            <option value="">（未選択）</option>
            {gcList.map((e) => (
              <option key={e.id} value={e.id}>
                {e.name}
                {e.isActive ? '' : ' [無効]'}
              </option>
            ))}
          </select>
          <p className="text-xs text-[var(--text-muted)] mt-1">
            設定 &gt; 通知 &gt; チャット管理 の Google Chat に登録したものから選びます。
          </p>
        </div>

        {/* 通知トグル */}
        <div>
          <Label>通知（メール / GC = Google Chat / CW = Chatwork）</Label>
          <div className="mt-1 rounded-md border border-[var(--border)] divide-y divide-[var(--border)] bg-[var(--bg-surface)]">
            {ACCOUNTING_NOTIFY_TRIGGERS.map((t) => {
              const ch = notify[t.id];
              const warnings = CHANNEL_KEYS.filter((k) => ch[k])
                .map((k) => missingTarget(k))
                .filter((w): w is string => !!w);
              const exampleOpen = openExample === t.id;
              return (
                <div key={t.id} className="p-3">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5">
                        <span className="text-sm font-medium text-[var(--text-primary)]">
                          {t.label}
                        </span>
                        <button
                          type="button"
                          onClick={() => setOpenExample(exampleOpen ? null : t.id)}
                          className={`p-0.5 rounded hover:bg-[var(--bg-hover)] ${
                            exampleOpen ? 'text-[var(--accent)]' : 'text-[var(--text-muted)]'
                          }`}
                          title="送信内容の例を表示"
                          aria-label={`${t.label} の送信内容の例を表示`}
                          aria-expanded={exampleOpen}
                        >
                          <Info className="size-3.5" />
                        </button>
                      </div>
                      <p className="text-[11px] text-[var(--text-muted)] mt-0.5">{t.when}</p>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      {CHANNEL_KEYS.map((k) => (
                        <ChannelPill
                          key={k}
                          on={ch[k]}
                          label={ACCOUNTING_NOTIFY_CHANNEL_LABELS[k]}
                          onClick={() => togglePill(t.id, k)}
                        />
                      ))}
                    </div>
                  </div>
                  {warnings.length > 0 && (
                    <p className="text-[11px] text-[var(--accent-amber)] mt-1.5">
                      {warnings.join(' / ')}（この送り方では送信されません）
                    </p>
                  )}
                  {!ch.mail && !ch.googleChat && !ch.chatwork && (
                    <p className="text-[11px] text-[var(--text-muted)] mt-1.5">
                      すべてオフ: 通知しません
                    </p>
                  )}
                  {exampleOpen && (
                    <AccountingNoticeExample trigger={t.id} mailSource={t.mailSource} />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── フォーム通知 (旧「フォーム通知」タブ) ── */}
      <section className="border-t border-[var(--border)] pt-6">
        <SectionTitle
          title="フォーム通知"
          description="フォームの入力タイプ「返答要求（チェックボックス）」にチェックが入った状態で送信された回答の通知方法です。"
        />
        <Label>返答待ち回答の通知先</Label>
        <div className="space-y-2 mt-1">
          {FORM_CHANNEL_OPTIONS.map((opt) => (
            <label
              key={opt.value}
              className={`flex gap-3 p-3 rounded border cursor-pointer transition-colors ${
                formChannel === opt.value
                  ? 'border-[var(--accent)] bg-[var(--accent-subtle)]'
                  : 'border-[var(--border)] bg-[var(--bg-surface)] hover:bg-[var(--bg-hover)]'
              }`}
            >
              <input
                type="radio"
                name="form-reply-notify-channel"
                className="mt-1"
                value={opt.value}
                checked={formChannel === opt.value}
                onChange={() => setFormChannel(opt.value)}
              />
              <span className="min-w-0">
                <span className="block text-sm font-medium">
                  {FORM_REPLY_NOTIFY_CHANNEL_LABELS[opt.value]}
                </span>
                <span className="block text-xs text-[var(--text-secondary)] mt-0.5">
                  {opt.description}
                </span>
              </span>
            </label>
          ))}
        </div>
        {formMailSelected && (
          <div className="mt-3 p-3 rounded border border-[var(--border)] bg-[var(--bg-elevated)]">
            <p className="text-xs text-[var(--text-secondary)]">
              メール通知の宛先:{' '}
              {autoMailAccount ? (
                <span className="font-medium">{autoMailAccount.email}</span>
              ) : (
                <span className="text-[var(--accent-amber)]">
                  送信可能なメールアカウントがありません
                </span>
              )}
            </p>
            <p className="text-xs text-[var(--text-muted)] mt-1">
              宛先はこのページの
              <strong>送信元メールアドレス（自動メール用デフォルト）</strong>
              に連動します。
            </p>
          </div>
        )}
      </section>

      <div className="pt-2">
        <Button type="button" variant="primary" onClick={handleSave} disabled={update.isPending}>
          {update.isPending ? '保存中…' : '保存'}
        </Button>
      </div>
    </div>
  );
}

function buildNotifyState(
  config: Parameters<typeof resolveAccountingNotifyChannels>[0],
): NotifyState {
  const out = {} as NotifyState;
  for (const t of ACCOUNTING_NOTIFY_TRIGGERS) {
    out[t.id] = resolveAccountingNotifyChannels(config, t.id);
  }
  return out;
}
